import { ActionTree, GetterTree, MutationTree } from "vuex";
import { RootState } from "vuefront-store";
import {
  ACCOUNT_CREATE_RETURN_REQUEST,
  ACCOUNT_ORDER,
  ACCOUNT_ORDERS,
  ACCOUNT_REORDER_SAFE,
  ACCOUNT_RETURN_CONTEXT,
} from "~/core/account/graphql";

export type Money = { raw: number; currency: string; formatted: string };
export type OrderSummary = {
  id: string;
  number: string;
  placedAt: string;
  status: string;
  itemCount: number;
  total: Money;
};
export type State = {
  items: OrderSummary[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  loading: boolean;
  loadingMore: boolean;
  error: string;
  detail: any | null;
  detailLoading: boolean;
  detailError: string;
  notFound: boolean;
  listRequest: number;
  detailRequest: number;
  returnEligibility: any | null;
  returnReasons: any[];
  returns: any[];
  returnLoading: boolean;
  returnError: string;
  returnSubmitting: boolean;
  returnResult: any | null;
  returnRequestId: string;
  reorderPending: boolean;
  reorderError: string;
  reorderResult: any | null;
  reorderRequestId: string;
};

export const state = (): State => ({
  items: [],
  page: 1,
  size: 10,
  totalItems: 0,
  totalPages: 0,
  loading: false,
  loadingMore: false,
  error: "",
  detail: null,
  detailLoading: false,
  detailError: "",
  notFound: false,
  listRequest: 0,
  detailRequest: 0,
  returnEligibility: null,
  returnReasons: [],
  returns: [],
  returnLoading: false,
  returnError: "",
  returnSubmitting: false,
  returnResult: null,
  returnRequestId: "",
  reorderPending: false,
  reorderError: "",
  reorderResult: null,
  reorderRequestId: "",
});

export const getters: GetterTree<State, RootState> = {
  items: (state) => state.items,
  pagination: (state) => ({
    page: state.page,
    size: state.size,
    totalItems: state.totalItems,
    totalPages: state.totalPages,
  }),
  hasMore: (state) => state.page < state.totalPages,
  loading: (state) => state.loading,
  loadingMore: (state) => state.loadingMore,
  error: (state) => state.error,
  detail: (state) => state.detail,
  detailLoading: (state) => state.detailLoading,
  detailError: (state) => state.detailError,
  notFound: (state) => state.notFound,
  returnEligibility: (state) => state.returnEligibility,
  returnReasons: (state) => state.returnReasons,
  returns: (state) => state.returns,
  returnLoading: (state) => state.returnLoading,
  returnError: (state) => state.returnError,
  returnSubmitting: (state) => state.returnSubmitting,
  returnResult: (state) => state.returnResult,
  reorderPending: (state) => state.reorderPending,
  reorderError: (state) => state.reorderError,
  reorderResult: (state) => state.reorderResult,
};

export const mutations: MutationTree<State> = {
  beginList(state, append: boolean) {
    state.listRequest += 1;
    state.loading = !append;
    state.loadingMore = append;
    state.error = "";
    if (!append) state.items = [];
  },
  applyList(state, { payload, requestId, append }: any) {
    if (requestId !== state.listRequest) return;
    const next = Array.isArray(payload?.items) ? payload.items : [];
    state.items = append
      ? [
          ...state.items,
          ...next.filter(
            (item: any) =>
              !state.items.some(
                (existing) => String(existing.id) === String(item.id)
              )
          ),
        ]
      : next;
    state.page = Number(payload?.page || 1);
    state.size = Number(payload?.size || state.size);
    state.totalItems = Number(payload?.totalItems || 0);
    state.totalPages = Number(payload?.totalPages || 0);
    state.loading = false;
    state.loadingMore = false;
  },
  failList(state, { requestId, message }: any) {
    if (requestId !== state.listRequest) return;
    state.error = message;
    state.loading = false;
    state.loadingMore = false;
  },
  beginDetail(state) {
    state.detailRequest += 1;
    state.detail = null;
    state.detailLoading = true;
    state.detailError = "";
    state.notFound = false;
    state.returnEligibility = null;
    state.returnReasons = [];
    state.returns = [];
    state.returnResult = null;
    state.returnRequestId = "";
    state.reorderResult = null;
    state.reorderError = "";
    state.reorderRequestId = "";
  },
  applyDetail(state, { detail, requestId }: any) {
    if (requestId !== state.detailRequest) return;
    state.detail = detail;
    state.notFound = !detail;
    state.detailLoading = false;
  },
  failDetail(state, { requestId, message }: any) {
    if (requestId !== state.detailRequest) return;
    state.detailError = message;
    state.detailLoading = false;
  },
  beginReturnContext(state) {
    state.returnLoading = true;
    state.returnError = "";
  },
  applyReturnContext(state, payload: any) {
    state.returnEligibility = payload?.accountReturnEligibilityV2 || null;
    state.returnReasons = payload?.accountReturnReasonsV2 || [];
    state.returns = payload?.accountReturnsV2 || [];
    state.returnLoading = false;
  },
  failReturnContext(state, message: string) {
    state.returnLoading = false;
    state.returnError = message;
  },
  beginReturnSubmit(state) {
    state.returnSubmitting = true;
    state.returnError = "";
    state.returnResult = null;
    if (!state.returnRequestId) state.returnRequestId = requestId();
  },
  finishReturnSubmit(state, result: any) {
    state.returnSubmitting = false;
    state.returnResult = result;
    // A server response means this idempotent intent has reached a terminal
    // result (including validation failures recorded by the backend). Keep the
    // key only across transport failures, where retrying the same request is
    // required to be safe.
    state.returnRequestId = "";
    state.returnError = result?.ok
      ? ""
      : result?.error?.message || returnSubmitError;
  },
  failReturnSubmit(state) {
    state.returnSubmitting = false;
    state.returnError = returnSubmitError;
  },
  beginReorder(state) {
    state.reorderPending = true;
    state.reorderError = "";
    state.reorderResult = null;
    if (!state.reorderRequestId) state.reorderRequestId = requestId();
  },
  finishReorder(state, result: any) {
    state.reorderPending = false;
    state.reorderResult = result;
    state.reorderRequestId = "";
    state.reorderError = result?.ok
      ? ""
      : result?.error?.message || reorderError;
  },
  failReorder(state) {
    state.reorderPending = false;
    state.reorderError = reorderError;
  },
  clearPrivate(current) {
    Object.assign(current, {
      ...state(),
      listRequest: current.listRequest + 1,
      detailRequest: current.detailRequest + 1,
    });
  },
};

const loadError =
  "We couldn’t load your orders. Check your connection and try again.";
const detailError =
  "We couldn’t load this order. Check your connection and try again.";
const returnLoadError =
  "We couldn’t load the return request details. Check your connection and try again.";
const returnSubmitError =
  "We couldn’t submit this return request. Check your connection and try again.";
const reorderError =
  "We couldn’t reorder these items. Your cart may be unchanged; try again safely.";
const requestId = () => {
  const cryptoApi =
    typeof globalThis !== "undefined" ? globalThis.crypto : null;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function")
    return cryptoApi.randomUUID();
  return `request-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const actions: ActionTree<State, RootState> = {
  async load({ state, commit }, { page = 1, append = false } = {}) {
    if (typeof window === "undefined") return { ok: true, ssrSkipped: true };
    commit("beginList", append);
    const requestId = state.listRequest;
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_ORDERS,
        variables: { page, size: state.size },
        fetchPolicy: "network-only",
      });
      commit("applyList", { payload: data.accountOrdersV2, requestId, append });
      return { ok: true, page: data.accountOrdersV2 };
    } catch {
      commit("failList", { requestId, message: loadError });
      return { ok: false, error: loadError };
    }
  },
  loadMore({ state, dispatch }) {
    if (state.loadingMore || state.page >= state.totalPages)
      return { ok: true, unchanged: true };
    return dispatch("load", { page: state.page + 1, append: true });
  },
  async loadDetail({ state, commit }, id: string) {
    if (typeof window === "undefined") return { ok: true, ssrSkipped: true };
    commit("beginDetail");
    const requestId = state.detailRequest;
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_ORDER,
        variables: { id },
        fetchPolicy: "network-only",
      });
      commit("applyDetail", { detail: data.accountOrderV2 || null, requestId });
      return {
        ok: Boolean(data.accountOrderV2),
        notFound: !data.accountOrderV2,
      };
    } catch {
      commit("failDetail", { requestId, message: detailError });
      return { ok: false, error: detailError };
    }
  },
  async loadReturnContext({ commit }, orderId: string) {
    if (typeof window === "undefined") return { ok: true, ssrSkipped: true };
    commit("beginReturnContext");
    try {
      const { data } = await this.$vfapollo.query({
        query: ACCOUNT_RETURN_CONTEXT,
        variables: { orderId },
        fetchPolicy: "network-only",
      });
      commit("applyReturnContext", data);
      return { ok: Boolean(data.accountReturnEligibilityV2), data };
    } catch {
      commit("failReturnContext", returnLoadError);
      return { ok: false, error: returnLoadError };
    }
  },
  async submitReturn({ state, commit, dispatch }, { orderId, lines }: any) {
    if (state.returnSubmitting) return { ok: false, busy: true };
    commit("beginReturnSubmit");
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_CREATE_RETURN_REQUEST,
        variables: {
          input: { requestId: state.returnRequestId, orderId, lines },
        },
      });
      const result = data.accountCreateReturnRequestV2;
      commit("finishReturnSubmit", result);
      if (result?.ok) await dispatch("loadReturnContext", String(orderId));
      return result;
    } catch {
      commit("failReturnSubmit");
      return { ok: false, error: { message: returnSubmitError } };
    }
  },
  async reorder({ state, commit, dispatch }, orderId: string) {
    if (state.reorderPending) return { ok: false, busy: true };
    commit("beginReorder");
    try {
      const { data } = await this.$vfapollo.mutate({
        mutation: ACCOUNT_REORDER_SAFE,
        variables: {
          input: { orderId, requestId: state.reorderRequestId },
        },
      });
      const result = data.accountReorderSafeV2;
      commit("finishReorder", result);
      if (result?.ok) {
        await dispatch("store/cart/load", {}, { root: true });
      }
      return result;
    } catch {
      commit("failReorder");
      return { ok: false, error: { message: reorderError } };
    }
  },
};
